import { ArrowLeft, CheckIcon, Close } from 'assets/icons';
import Button from 'components/button';
import EmptyState from 'components/EmptyState/EmptyState';
import UseModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import SubNavbar from 'components/SubNavbar/SubNavbar';
import Tooltip from 'components/tooltips/Tooltips';
import { dateWordMonth } from 'helper/helper';
import UseContext from 'hooks/useContext';
import useImportedTimeSheet from 'hooks/useImportedTimeSheet';
import useSlideOver from 'components/SlideOver/useSlideOver';
import TBodyHoursCompany from 'pages/timeSheet/components/tBodyHours';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import MediaQuery from 'react-responsive';

// ── Skeleton ──

const FicheHourSkeleton = () => (
  <div className="flex flex-col gap-6 items-center justify-center tab:flex-row">
    <div className="w-full tab:max-w-[654px] bg-white flex flex-col gap-4 p-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-64 mb-4" />
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 bg-gray-200 rounded" />
        ))}
      </div>
    </div>
    <MediaQuery minWidth={1100}>
      <div className="w-full tab:w-[450px] bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-32 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-5 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    </MediaQuery>
  </div>
);

// ── Info Card (shared mobile/desktop) ──

const STATUS_LABELS = { 0: 'Non complété', 1: 'À vérifier', 2: 'Litige', 3: 'Vérifié' };
const STATUS_LIST = [
  { label: 'Non complété', value: 0 },
  { label: 'À vérifier', value: 1 },
  { label: 'Litige', value: 2 },
  { label: 'Vérifié', value: 3 },
];

/** API / aggregation may return validated as number or string ("0", "3", …). */
const normalizeTimeSheetValidated = (v) => {
  const n = Number(v);
  return [0, 1, 2, 3].includes(n) ? n : 0;
};

const TimeSheetInfoCard = ({ data, missionTitle, contentTitle, onOpenTempWorker, onOpenMission, statusSelector, className = '' }) => {
  const contractId = data?.mission?.contract_company?.[0]?.id_bestt || data?.mission?.contract_tempworker?.[0]?.id_bestt;
  const userId = data?.id_user || data?.user?.id_user;
  const hasMission = data?.id_mission || data?.id_mission_reel;

  const InfoRow = ({ label, children }) => (
    <div className="flex justify-between items-center text-sm">
      <span className="font-bold text-gray-900">{label}</span>
      {children}
    </div>
  );

  const ClickableValue = ({ text, tooltip, onClick }) => (
    <Tooltip text={tooltip} placement="top">
      <span
        className="text-gray-500 font-medium text-right max-w-[200px] truncate cursor-pointer hover:underline decoration-dashed transition-colors"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        {text}
      </span>
    </Tooltip>
  );

  const StaticValue = ({ text }) => (
    <span className="text-gray-500 font-medium text-right max-w-[200px] truncate">{text}</span>
  );

  const fullName = `${data?.user?.firstname || ''} ${data?.user?.lastname || ''}`.trim() || '-';
  const title = data?.mission?.title || missionTitle || contentTitle || '-';

  return (
    <div className={`w-full bg-white rounded-xl border border-gray-100 p-6 ${className}`}>
      <h3 className="font-bold text-lg text-gray-900 mb-6">Informations</h3>
      <div className="flex flex-col gap-6">
        <InfoRow label="Période">
          <StaticValue text={`${dateWordMonth(data?.periode_start)} > ${dateWordMonth(data?.periode_end)}`} />
        </InfoRow>
        <InfoRow label="Intérimaire">
          {userId
            ? <ClickableValue text={fullName} tooltip="Voir la fiche" onClick={() => onOpenTempWorker(userId)} />
            : <StaticValue text={fullName} />
          }
        </InfoRow>
        <InfoRow label="Mission">
          {hasMission
            ? <ClickableValue text={title} tooltip="Voir la mission" onClick={onOpenMission} />
            : <StaticValue text={title} />
          }
        </InfoRow>
        <InfoRow label="Contrat">
          <StaticValue text={contractId ? `n°${contractId}` : '-'} />
        </InfoRow>
        {statusSelector && (
          <InfoRow label="Statut">
            <div className="max-w-[200px] w-full shrink-0">
              {statusSelector}
            </div>
          </InfoRow>
        )}
      </div>
    </div>
  );
};

// ── Component ──

const FicheHour = () => {
  const {
    setObjID, setObjType, refreshTime, setTotalH, content,
    timeCheck, setTimeCheck, setUpdatedTimesheet, setMissionInitialPage,
    userData,
  } = UseContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggleSlideOver } = useSlideOver();
  const [datas, setDatas] = useState([]);
  const [nMission, setNMission] = useState('');
  const { toggle } = UseModal();
  const [isTimeSheetImported, setIsTimeSheetImported] = useState(false);
  const [isWaiting, setIsWaiting] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');
  const { toggleImported } = useImportedTimeSheet();

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.getTimeSheetInfo(
      { id },
      (data) => {
        setDatas(data);
        setObjID(data[0].id_time_sheet);
        setIsTimeSheetImported(data[0].is_imported_bestt);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') { console.log(err); setIsWaiting(false); } }
    );
  }, [refreshTime, id]);

  useEffect(() => {
    if (datas.length > 0 && datas[0]?.id_mission) {
      ApiDatabase.getInfoMission(
        { id: datas[0].id_mission },
        (data) => { setNMission(data.title); setObjType(data.title); },
        (err) => { if (err.name !== 'CanceledError') console.log(err); }
      );
    }
  }, [datas[0]?.id_mission]);

  const getSeconds = (s) => s.split('h').reduce((acc, curr) => acc * 60 + +curr, 0);

  const { allDaysValid, allTimeSheetValid, allTimeSheetClose } = useMemo(() => {
    const days = datas[0]?.days || [];
    let totalSeconds = 0;
    let valid = true;
    days.forEach((d) => {
      if (d.day_hours_ent) totalSeconds += getSeconds(d.day_hours_ent);
      if (normalizeTimeSheetValidated(d.validated) === 0) valid = false;
    });
    setTotalH(totalSeconds);

    const checkValues = Object.values(timeCheck);
    const hasChecks = checkValues.length > 0;
    return {
      allDaysValid: valid,
      allTimeSheetValid: hasChecks && checkValues.every((v) => v === 2),
      allTimeSheetClose: hasChecks && checkValues.every((v) => v === 3),
    };
  }, [datas, timeCheck]);

  const handleSubmit = () => {
    if (allDaysValid) toggle('doc_u_validWeek');
    else toast.error('Veuillez compléter la semaine');
  };

  const toggleAll = (targetState) => {
    const allAlready = Object.values(timeCheck).every((v) => v === targetState);
    const newVal = allAlready ? '' : targetState;
    const updated = {};
    for (const key in timeCheck) updated[key] = newVal;
    setTimeCheck(updated);
  };

  const handleNext = (e) => {
    e.preventDefault();
    const allSet = Object.values(timeCheck).every((v) => v !== '');
    if (allSet) { setObjID(id); toggle('doc_u_validWeek'); }
    else toast.error('Veuillez valider ou refuser toutes les heures.');
  };

  useEffect(() => {
    const initial = {};
    datas[0]?.days?.forEach((i) => { initial[i._id] = ''; });
    setTimeCheck(initial);
  }, [datas]);

  const record = datas[0];
  const validatedNum = normalizeTimeSheetValidated(record?.validated);
  const canEditTimeSheetStatus = userData?.role === 500;

  const handleToggleImported = (isImported, idTimeSheet) => {
    toggleImported(isImported, idTimeSheet, (imported) => {
      setDatas((prev) => prev.map((item) => item._id === idTimeSheet ? { ...item, is_imported_bestt: imported } : item));
      setIsTimeSheetImported(imported);
      setUpdatedTimesheet((prev) => ({ ...prev, [idTimeSheet]: imported }));
    });
  };

  const handleStatusChange = (selectedList) => {
    if (!canEditTimeSheetStatus) return;
    const selected = selectedList.find((item) => item.checked);
    const currentRecord = datas[0];
    if (!selected || !currentRecord) return;
    setStatusLoading(true);
    ApiDatabase.updateTimeSheetStatus(
      { id: currentRecord._id, validated: selected.value, token },
      () => {
        setDatas((prev) => prev.map((item) =>
          item._id === currentRecord._id ? { ...item, validated: selected.value } : item
        ));
        setStatusLoading(false);
        toast.success('Statut mis à jour.');
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          toast.error('Erreur lors de la mise à jour du statut.');
        }
        setStatusLoading(false);
      }
    );
  };

  const statusSelector = canEditTimeSheetStatus ? (
    <StyledInput
      type="selectMultipleRadio"
      value={STATUS_LABELS[validatedNum]}
      list={STATUS_LIST.map((item) => ({ ...item, checked: item.value === validatedNum }))}
      onChange={handleStatusChange}
      isDisabled={statusLoading}
      className="w-full border rounded-md bg-white text-gray-900 ft-sm"
      classNameValue="px-4 py-2 flex items-center justify-end text-right truncate"
    />
  ) : (
    <span className="text-gray-500 font-medium text-right max-w-[200px] truncate block">
      {STATUS_LABELS[validatedNum]}
    </span>
  );

  const openTempWorkerInfo = (userId) => {
    setObjID(userId);
    toggleSlideOver('get_temp_worker');
  };

  const openMissionInfo = () => {
    if (setMissionInitialPage) setMissionInitialPage(3);
    if (datas[0]?.id_mission) {
      setObjID(datas[0].id_mission);
      toggleSlideOver('get_mission');
    } else if (datas[0]?.id_mission_reel) {
      ApiDatabase.searchMission(
        { token, search: datas[0].id_mission_reel.toString(), optionSearch: 'id_mission' },
        (data) => {
          if (data.missions?.length > 0) {
            const mission = data.missions.find(m => m.id_mission?.toString() === datas[0].id_mission_reel?.toString());
            const missionId = (mission || data.missions[0])?.id_mission_real;
            if (missionId) { setObjID(missionId); toggleSlideOver('get_mission'); }
          }
        },
        (err) => console.error('Error fetching mission:', err)
      );
    }
  };

  return (
    <div className="flex flex-col w-full h-full">
      <SubNavbar>
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/timeSheet'); }}
        >
          <ArrowLeft w={16} color="#111827" />
          <div className="text-sm font-medium text-gray-900">Retour</div>
        </div>
        {validatedNum === 3 && (
          <div className="border rounded py-2 px-4 text-sm import-button-fiche-hour">
            <label className="flex items-center gap-2 cursor-pointer">
              <StyledInput
                type="checkbox"
                checked={isTimeSheetImported}
                onChange={(e) => handleToggleImported(e.target.checked, record?._id)}
              />
              <div>Importé</div>
            </label>
          </div>
        )}
      </SubNavbar>

      <div className="flex-1 overflow-y-auto p-3 tab:p-4 lg:p-8">
        {isWaiting ? (
          <FicheHourSkeleton />
        ) : record ? (
          <div className="flex flex-col gap-12 items-center justify-center tab:flex-row">
            <div className="w-full tab:max-w-[654px] bg-white flex flex-col gap-4 header-fiche-hour">
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Relevé d'heure n°{record?.id_time_sheet}
                </h1>
                <div className="flex justify-between items-center px-2 ft-2xl text-gray-900">
                  {validatedNum === 1 && (
                    <Button size="SM" onClick={handleSubmit}>Valider la semaine</Button>
                  )}
                </div>
              </div>

              {/* Informations — mobile/tablet */}
              <MediaQuery maxWidth={1099}>
                <TimeSheetInfoCard
                  data={record}
                  missionTitle={nMission}
                  contentTitle={content?.title}
                  onOpenTempWorker={openTempWorkerInfo}
                  onOpenMission={openMissionInfo}
                  statusSelector={statusSelector}
                />
              </MediaQuery>

              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-6 items-center ft-sm text-gray-700 font-medium">
                  JOUR
                  <span className="text-gray-500">
                    {record?.total_hours_ent ? `heures travaillées (${record.total_hours_ent})` : null}
                  </span>
                </div>
                {validatedNum !== 0 && (
                  <div className="flex items-center justify-end gap-2">
                    {validatedNum !== 2 && (
                      <Button
                        color={allTimeSheetValid ? 'REFUSED' : 'ICON'}
                        size="ICON"
                        onClick={() => toggleAll(2)}
                      >
                        <Close wh={20} color={allTimeSheetValid ? '#B91C1C' : '#374151'} />
                      </Button>
                    )}
                    <Button
                      color={allTimeSheetClose ? 'DEFAULT' : 'ICON'}
                      size="ICON"
                      onClick={() => toggleAll(3)}
                      className={allTimeSheetClose ? 'bg-green-100 hover:bg-green-200' : ''}
                    >
                      <CheckIcon w={15} color={allTimeSheetClose ? '#047857' : '#374151'} />
                    </Button>
                  </div>
                )}
              </div>

              <TBodyHoursCompany champsBody={record} />

              {validatedNum !== 0 && (
                <div className="flex">
                  <div className="ml-auto">
                    <Button size="LG" onClick={handleNext}>Confirmer</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Informations — desktop */}
            <MediaQuery minWidth={1100}>
              <TimeSheetInfoCard
                data={record}
                missionTitle={nMission}
                contentTitle={content?.title}
                onOpenTempWorker={openTempWorkerInfo}
                onOpenMission={openMissionInfo}
                className="tab:w-[450px] flex-shrink-0 h-fit sticky top-24"
                statusSelector={statusSelector}
              />
            </MediaQuery>
          </div>
        ) : (
          <EmptyState type="time_sheet" />
        )}
      </div>
    </div>
  );
};

export default FicheHour;
